import {
    extractColumnGroupsFromMultilingualStrings,
    extractMultilingualStringsFromColumnGroups,
} from "features/product/functions/columnGroups";
import { SubProductAction } from "features/product/types/actions.subproduct";
import { MultilingualString } from "features/shared/types/string";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "services/redux/rootReducer";
import styled from "styled-components";
import SmartUpdateInput from "../../../../../shared/components/form/components/smartUpdateInput";
import useCurrentTableState from "../hooks/useCurrentTableState";

const TableColumnGroups: React.FC = () => {
    const [rawColumnGroups, setRawColumnGroups] = useState<
        MultilingualString[]
    >([]);

    const { locale, groupingMode, readOnlyMode } = useSelector(
        (state) => state.subProducts
    );
    const dispatch = useDispatch();

    const { columnGroups, columns, rows } = useCurrentTableState();

    useEffect(() => {
        if (groupingMode) {
            setRawColumnGroups(
                extractMultilingualStringsFromColumnGroups(
                    columnGroups,
                    columns.length
                )
            );
        }
    }, [columnGroups, columns.length, dispatch, groupingMode]);

    const handleInputChange = (value: string, idx: number) => {
        const updatedRawColumnGroups = [
            ...rawColumnGroups.slice(0, idx),
            {
                ...rawColumnGroups[idx],
                [locale]: value,
            },
            ...rawColumnGroups.slice(idx + 1),
        ];
        setRawColumnGroups(updatedRawColumnGroups);
        dispatch({
            type: SubProductAction.SET_COLUMN_GROUPS,
            columnGroups: extractColumnGroupsFromMultilingualStrings(
                updatedRawColumnGroups
            ),
        });
    };

    const rawGroupings = rawColumnGroups.map((multilingualString, idx) => (
        <GroupCell key={idx}>
            <SmartUpdateInput
                onChangeHandler={(value) => handleInputChange(value, idx)}
                initialValue={multilingualString[locale]}
            />
        </GroupCell>
    ));

    const columnGroupings = columnGroups?.map(({ columnSize, name }, idx) => (
        <GroupCell columnSize={columnSize} key={idx} idx={idx}>
            {name[locale]}
        </GroupCell>
    ));

    return (
        <Container>
            {rows.length > 0 && (
                <Spacer width={readOnlyMode ? "3.6rem" : "12.6rem"} />
            )}
            {groupingMode ? (
                rawGroupings
            ) : columnGroupings.length ? (
                columnGroupings
            ) : (
                <GroupCell />
            )}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    width: auto;
`;

const Spacer = styled.div<{ width?: string }>`
    width: ${(props) => props.width};
    background: ${(props) => props.theme.colors.neutral[200]};
    border-right: solid 0.5px ${(props) => props.theme.colors.neutral[300]};
`;

const GroupCell = styled.div<{
    columnSize?: number;
    idx?: number;
}>`
    flex: ${({ columnSize }) => columnSize || 1};

    // conditionally sets background depending on whether idx is even or odd
    background: ${(props) =>
        props.theme.colors.neutral[
            props.idx !== undefined && props.idx % 2 !== 0 ? 300 : 200
        ]};
    height: 1.8rem;

    display: grid;
    place-items: center;

    & > input {
        overflow: hidden;
        background: white;
        max-width: 80%;
    }
`;

export default TableColumnGroups;
